import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { injectJwtTokenIntoHeaders } from 'services/tokenLocalStorage';
import {
  ECrmDirectoryEntryType,
  IDirectoryEntry,
  IDirectorySearchParams,
  IDirectorySearchResponse,
  IDirectorySimpleSearchParams,
  IDirectorySimpleSearchResponse,
  IFilterSearch,
  IUpsertLink,
  IUpsertLinkResponse,
} from './types/DirectoryEntry';
import { IBootstrapCountry } from 'services/BackendApi/types/country';
import { ETypeValueType, ISREditableTypes, ITypeValue } from './types/types';
import {
  INewCompanyRequest,
  INewCompanyResponse,
  IUpdateCompanyRequest,
  IUpdateCompanyResponse,
} from './types/CrmCompanyTypes';
import {
  INewContactRequest,
  INewContactResponse,
  IUpdateContactRequest,
  IUpdateContactResponse,
} from './types/CrmContactTypes';
import { ICreateLeadRequest, ICreateLeadResponse, IUpdateLeadRequest } from './types/CrmLeadTypes';
import { ICrmErrorCollection } from './types/CrmErrorTypes';
import {
  ICrmActivity,
  ICrmActivityCategoryGroup,
  ISearchActivityRequest,
  ISearchActivityResponse,
  ISearchActivityParticipantsRequest,
  ISearchActivityParticipantsResponse,
  IUpsertActivityRequest,
} from './types/CrmActivityTypes';
import { IBulkChangeNotificationsStatusRequest, IExportNotificationsRequest, IExportNotificationsSuccessResponse, IGetUnviewedNotificationsCountResponse, INotificationSearchRequest, INotificationSearchResponse, ISubscribeToDirectoryEntryResponse } from './types/NotificationTypes';
import { ICrmSearchTagGroup, ICrmSearchTagsRequest } from './types/CrmTagTypes';
import { ITrackingEventInput } from 'integrations/trackingEvents/types';
import { getSessionId as getTrackingSessionId } from 'integrations/trackingEvents/session';
import { getSubSessionId as getTrackingSubSessionId } from 'integrations/trackingEvents/subSession';

export enum CrmEndpoints {
  EXAMPLES = 'v1/examples',
  COUNTRIES = 'v1/countries',
  TYPE_VALUES = 'v1/type-values',
  DIRECTORY = 'v1/directory',
  COMPANY = 'v1/company',
  CONTACT = 'v1/contact',
  LINK = 'v1/link',
  LEAD = 'v1/leads',
  ACTIVITY = 'v1/activity',
  NOTIFICATION = 'v1/notification',
  SUBSCRIPTION = 'v1/subscription',
  TAG = 'v1/tag',
  USER = 'v1/user',
  TRACKING_EVENTS = 'v1/tracking-events',
}

export class CRMApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  getExamples = async (): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.EXAMPLES}`;
    return this.client.get(endpoint);
  };

  postTypeValues = async (
    type: ETypeValueType,
    { enabled }: { enabled: boolean }
  ): Promise<AxiosResponse<ITypeValue[]>> => {
    const endpoint = `${CrmEndpoints.TYPE_VALUES}/${type}`;
    return this.client.post(endpoint, { enabled });
  };

  postTitleValues = async (
    type: ETypeValueType,
    { enabled }: { enabled: boolean }
  ): Promise<AxiosResponse<ITypeValue[]>> => {
    const endpoint = `${CrmEndpoints.TYPE_VALUES}/${type}`;
    return this.client.post(endpoint, { enabled });
  };

  postCountries = async (
    { isDestination }: { isDestination?: boolean } = { isDestination: undefined }
  ): Promise<AxiosResponse<IBootstrapCountry[]>> => {
    const endpoint = `${CrmEndpoints.COUNTRIES}`;
    const params = isDestination ? { isDestination } : {};
    return this.client.post(endpoint, params);
  };

  createCompany = async (data: INewCompanyRequest): Promise<AxiosResponse<INewCompanyResponse>> => {
    const endpoint = `${CrmEndpoints.COMPANY}`;
    return this.client.post(endpoint, { ...data });
  };

  updateCompany = async (data: IUpdateCompanyRequest): Promise<AxiosResponse<IUpdateCompanyResponse>> => {
    const { directoryId, ...rest } = data;
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryId}`;
    return this.client.patch(endpoint, { ...rest });
  };

  publishDirectoryEntry = async (directoryId: string): Promise<AxiosResponse<INewCompanyResponse>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryId}/publish`;
    return this.client.post(endpoint);
  };

  getDirectoryEntry = async (directoryId: string): Promise<AxiosResponse<IDirectoryEntry>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryId}`;
    return this.client.get(endpoint);
  };

  createContact = async (data: INewContactRequest): Promise<AxiosResponse<INewContactResponse>> => {
    const endpoint = `${CrmEndpoints.CONTACT}`;
    return this.client.post(endpoint, { ...data });
  };

  updateContact = async (data: IUpdateContactRequest): Promise<AxiosResponse<IUpdateContactResponse>> => {
    const { directoryId, ...rest } = data;
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryId}`;
    return this.client.patch(endpoint, { ...rest });
  };

  postDirectorySearch = async (params: IDirectorySearchParams): Promise<AxiosResponse<IDirectorySearchResponse>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/search`;
    return this.client.post(endpoint, params);
  };

  postDirectorySearchSimple = async (
    params: IDirectorySimpleSearchParams
  ): Promise<AxiosResponse<IDirectorySimpleSearchResponse>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/search/simple`;
    return this.client.post(endpoint, params);
  };

  postSubscriptionToAllNotifications = async (filter: IFilterSearch): Promise<AxiosResponse<{ total: number }>> => {
    const endpoint = `${CrmEndpoints.SUBSCRIPTION}/batch`;
    return this.client.post(endpoint, { filter });
  };

  deleteSubscriptionFromAllNotifications = async (filter: IFilterSearch): Promise<AxiosResponse<{ total: number }>> => {
    const endpoint = `${CrmEndpoints.SUBSCRIPTION}/batch`;
    return this.client.delete(endpoint, { data: { filter } });
  };

  linkDirectoryEntries = async (data: IUpsertLink): Promise<AxiosResponse<IUpsertLinkResponse>> => {
    const endpoint = `${CrmEndpoints.LINK}`;
    return this.client.post(endpoint, { ...data });
  };

  unlinkDirectoryEntries = async (linkId: string): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.LINK}`;
    return this.client.delete(`${endpoint}/${linkId}`);
  };

  createLead = async (data: ICreateLeadRequest): Promise<AxiosResponse<ICreateLeadResponse | ICrmErrorCollection>> => {
    const { directoryEntryId, ...rest } = data;
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryEntryId}/leads`;
    return this.client.post(endpoint, { ...rest });
  };

  updateLead = async (data: IUpdateLeadRequest): Promise<AxiosResponse<ICreateLeadResponse | ICrmErrorCollection>> => {
    const { leadId, ...rest } = data;
    const endpoint = `${CrmEndpoints.LEAD}/${leadId}`;
    return this.client.put(endpoint, { ...rest });
  };

  removeLead = async (leadId: string): Promise<AxiosResponse<void | ICrmErrorCollection>> => {
    const endpoint = `${CrmEndpoints.LEAD}/${leadId}`;
    return this.client.delete(endpoint);
  };

  createActivity = async (data: IUpsertActivityRequest): Promise<AxiosResponse<ICrmActivity>> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}`;
    return this.client.post(endpoint, { ...data });
  };

  searchActivity = async (data: ISearchActivityRequest): Promise<AxiosResponse<ISearchActivityResponse>> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}/search`;
    return this.client.post(endpoint, { ...data });
  };

  searchActivityParticipants = async (
    data: ISearchActivityParticipantsRequest
  ): Promise<AxiosResponse<ISearchActivityParticipantsResponse>> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}/participants/search`;
    return this.client.post(endpoint, { ...data });
  };

  deleteActivity = async (activityId: string): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}/${activityId}`;
    return this.client.delete(endpoint);
  };

  updateActivity = async (activityId: string, data: IUpsertActivityRequest): Promise<AxiosResponse<ICrmActivity>> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}/${activityId}`;
    return this.client.patch(endpoint, { ...data });
  };

  getCategories = async (): Promise<AxiosResponse<ICrmActivityCategoryGroup[]>> => {
    const endpoint = `${CrmEndpoints.ACTIVITY}/category/groups`;
    return this.client.get(endpoint);
  };

  searchNotifications = async (data: INotificationSearchRequest): Promise<AxiosResponse<INotificationSearchResponse>> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/search`;
    return this.client.post(endpoint, { ...data });
  };

  getUnviewedNotificationsCount = async (): Promise<AxiosResponse<IGetUnviewedNotificationsCountResponse>> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/count-unviewed`;
    return this.client.get(endpoint);
  };

  bulkMarkNotificationsViewed = async (data: IBulkChangeNotificationsStatusRequest): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/${data.startDate}/${data.endDate}`;
    return this.client.patch(endpoint, { viewed: data.viewed });
  };

  bulkMarkNotificationsRead = async (data: IBulkChangeNotificationsStatusRequest): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/${data.startDate}/${data.endDate}`;
    return this.client.patch(endpoint, { read: data.read });
  };

  markNotificationViewed = async (notificationId: string, viewed: boolean): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/${notificationId}`;
    return this.client.patch(endpoint, { viewed });
  };

  markNotificationRead = async (notificationId: string, read: boolean): Promise<AxiosResponse> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/${notificationId}`;
    // if user marks notification as read or unread, we also mark it as viewed
    return this.client.patch(endpoint, { read, viewed: true });
  };

  exportNotifications = async (data: IExportNotificationsRequest): Promise<AxiosResponse<IExportNotificationsSuccessResponse>> => {
    const endpoint = `${CrmEndpoints.NOTIFICATION}/export`;
    return this.client.post(endpoint, { ...data });
  };

  subscribeToDirectoryEntry = async (directoryEntryId: string, beUserId: string): Promise<AxiosResponse<ISubscribeToDirectoryEntryResponse>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryEntryId}/subscribers`;
    return this.client.post(endpoint, { beUserId });
  };

  unsubscribeFromDirectoryEntry = async (directoryEntryId: string, beUserId: string): Promise<AxiosResponse<ISubscribeToDirectoryEntryResponse>> => {
    const endpoint = `${CrmEndpoints.DIRECTORY}/${directoryEntryId}/subscribers/${beUserId}`;
    return this.client.delete(endpoint);
  };

  searchTags = async (request: ICrmSearchTagsRequest): Promise<AxiosResponse<ICrmSearchTagGroup[]>> => {
    const endpoint = `${CrmEndpoints.TAG}/search`;
    return this.client.post(endpoint, { ...request });
  };

  // Get editable contact and company types for the current SR user
  getSREditableTypes = async (): Promise<AxiosResponse<ISREditableTypes>> => {
    const endpoint = `${CrmEndpoints.USER}/sr-editable-types`;
    return this.client.get(endpoint);
  };

  // Post tracking events batch. uses fetch (not axios) to opt in to `keepalive`,
  // which is required for the visibilitychange/pagehide flush.
  postTrackingEvents = (events: ITrackingEventInput[], { keepalive = false }: { keepalive?: boolean } = {}): void => {
    const base = ParameterService.getParameters().CRM_BASE_URL;
    if (!base || events.length === 0) return;

    const headers = injectJwtTokenIntoHeaders({ 'Content-Type': 'application/json' });
    if (!headers.authorization) return;

    headers['x-session-id'] = getTrackingSessionId();
    headers['x-sub-session-id'] = getTrackingSubSessionId();

    fetch(`${base.replace(/\/$/, '')}/${CrmEndpoints.TRACKING_EVENTS}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      credentials: 'include',
      keepalive,
    }).catch(() => {
      /* tracking must never break the app */
    });
  };
}

export const makeCRMApi = (): CRMApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.CRM_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
  });

  const clientWrapper = new AxiosWrapper(client);

  return new CRMApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
